import React, { useState, useRef, ReactNode, useEffect } from 'react';
import {
  Popover,
  Checkbox,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Box,
  Button,
  Stack,
  Paper,
  Typography,
  Menu,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';

const MarketSearchBar = ({ OnBoardingSearchBox, addMarketAfterSearch }) => {
  const handleOnSearchOptionClick = (_, value) => {
    addMarketAfterSearch({
      label: value.searchName,
      details: value,
    });
  };

  return OnBoardingSearchBox('', true, handleOnSearchOptionClick, 'compare', 'compare');
};

interface OptionType {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
  isExpandable?: boolean;
  isChild?: boolean;
  parentValue?: string | null;
}

interface FilterDropdownProps {
  label: string;
  options: OptionType[];
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  multiSelect?: boolean;
  singleSelect?: boolean;
  placeholder?: string;
  selectedCountBadge?: boolean;
  button?: ReactNode;
  hideSearch?: boolean;
  extraStyle?: React.CSSProperties;
  onNoResultsAction?: () => void;
  OnBoardingSearchBox?: any;
  setMarketSearchInfo?: (val: { searchName: string; countryCode: string } | null) => void;
  setMarketComparisons?: any;
  isFreeUser?: boolean;
  customContent?: ReactNode | ((props: { handleClose: () => void }) => ReactNode);
}

// Helper for parent option selection
const handleParentSelect = (
  optionValue: string,
  expandedParents: string[],
  options: OptionType[],
  newValue: string[],
  newExpandedParents: string[]
) => {
  if (!expandedParents.includes(optionValue)) {
    newExpandedParents.push(optionValue);
  }
  const children = options.filter(
    o => o.isChild && o.parentValue === optionValue
  ).map(o => o.value);
  newValue = newValue.filter(v => !children.includes(v));
  if (newValue.includes(optionValue)) {
    newValue = newValue.filter(v => v !== optionValue);
  } else {
    newValue.push(optionValue);
  }
  return { newValue, newExpandedParents };
};

// Helper for child option selection
const handleChildSelect = (
  optionValue: string,
  option: OptionType,
  expandedParents: string[],
  newValue: string[],
  newExpandedParents: string[]
) => {
  newValue = newValue.filter(v => v !== option.parentValue);
  if (newValue.includes(optionValue)) {
    newValue = newValue.filter(v => v !== optionValue);
  } else {
    newValue.push(optionValue);
  }
  if (!expandedParents.includes(option.parentValue!)) {
    newExpandedParents.push(option.parentValue!);
  }
  return { newValue, newExpandedParents };
};

// Helper for regular option selection
const handleRegularSelect = (
  optionValue: string,
  newValue: string[]
) => {
  if (newValue.includes(optionValue)) {
    return newValue.filter(v => v !== optionValue);
  } else {
    return [...newValue, optionValue];
  }
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  multiSelect = false,
  singleSelect = false,
  placeholder = '',
  selectedCountBadge = false,
  button,
  hideSearch = false,
  extraStyle = {},
  onNoResultsAction,
  OnBoardingSearchBox,
  setMarketSearchInfo,
  setMarketComparisons,
  isFreeUser,
  customContent,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [usExpanded, setUsExpanded] = useState(false);
  const [expandedParents, setExpandedParents] = useState<string[]>([]);
  const [isMarketSearch, setIsMarketSearch] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const isLocationFilter = label === 'Location';

  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      inputRef.current && inputRef.current.focus();
    }, 100);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setIsMarketSearch(false);
  };

  useEffect(() => {
    if (isLocationFilter && search) {
      const searchLower = search.toLowerCase();
      const matchesState = options.some(
        opt => opt.isChild && opt.parentValue === 'US' && opt.label.toLowerCase().includes(searchLower)
      );
      if (matchesState && !expandedParents.includes('US')) {
        setExpandedParents(prev => [...prev, 'US']);
      }
    } else if (isLocationFilter && !search && expandedParents.includes('US')) {
    }
  }, [search, isLocationFilter, options, expandedParents]);

  const filteredOptions = options.filter(opt => {
    const labelLower = opt.label.toLowerCase();
    const searchLower = search.toLowerCase();

    const isParentExpanded = opt.isChild && opt.parentValue ? expandedParents.includes(opt.parentValue) : true;

    if (!searchLower) {
      return opt.isChild ? isParentExpanded : true;
    }

    const searchMatchesOption = labelLower.includes(searchLower);

    if (isLocationFilter) {
      const isUS = opt.value === 'US';
      const isUSChild = opt.isChild && opt.parentValue === 'US';
      const searchMatchesUSString = 'united states'.includes(searchLower);

      if (isUSChild) {
        return searchMatchesOption || (searchMatchesUSString && expandedParents.includes('US'));
      } else if (isUS) {
        const searchMatchesAnyState = options.some(
          o => o.isChild && o.parentValue === 'US' && o.label.toLowerCase().includes(searchLower)
        );
        return searchMatchesUSString || searchMatchesAnyState;
      } else {
        return searchMatchesOption && (opt.isChild ? isParentExpanded : true);
      }
    } else {
      return searchMatchesOption && (opt.isChild ? isParentExpanded : true);
    }
  });

  const handleSelect = (optionValue: string) => {
    if (multiSelect && Array.isArray(value)) {
      const option = options.find(opt => opt.value === optionValue);
      if (!option) return;
      let newValue = [...value];
      let newExpandedParents = [...expandedParents];

      if (!option.isChild && option.isExpandable) {
        ({ newValue, newExpandedParents } = handleParentSelect(optionValue, expandedParents, options, newValue, newExpandedParents));
      } else if (option.isChild && option.parentValue) {
        ({ newValue, newExpandedParents } = handleChildSelect(optionValue, option, expandedParents, newValue, newExpandedParents));
      } else {
        newValue = handleRegularSelect(optionValue, newValue);
      }
      setExpandedParents(newExpandedParents);
      onChange(newValue);
      return;
    }
    if (singleSelect) {
      onChange(optionValue);
      handleClose();
    }
  };

  const handleDeleteChip = (chipValue: string) => {
    if (multiSelect && Array.isArray(value)) {
      onChange(value.filter(v => v !== chipValue));
    }
  };

  const selectedOptions =
    multiSelect && Array.isArray(value)
      ? options.filter(opt => value.includes(opt.value))
      : singleSelect && typeof value === 'string'
      ? options.filter(opt => opt.value === value)
      : [];

  const toggleSearchBar = () => {
    setIsMarketSearch(!isMarketSearch);
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', ...extraStyle }}>
      {button ? (
        <Box onClick={handleOpen} sx={{ cursor: 'pointer', position: 'relative' }}>
          {button}
        </Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 999,
            px: 2,
            py: 1,
            minWidth: 120,
            boxShadow: 1,
            textTransform: 'none',
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1303,
            '&:hover': { boxShadow: 3, backgroundColor: 'white' },
          }}
          endIcon={<ArrowDropDownIcon />}
        >
          <Box sx={{ mr: 1, fontWeight: 500, color: 'grey.800' }}>{label}</Box>
          {selectedCountBadge && selectedOptions.length > 0 && (
            <Badge badgeContent={selectedOptions.length} color="primary" sx={{ mr: 1 }} />
          )}
          <Stack direction="row" spacing={0.5} sx={{ maxWidth: 120, overflowX: 'auto' }}>
            {selectedOptions.map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onDelete={multiSelect ? () => handleDeleteChip(opt.value) : undefined}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Stack>
        </Button>
      )}
      <Menu
        id={`${label.toLowerCase()}-menu`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ style: { minWidth: anchorEl?.offsetWidth } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {customContent ? (
          typeof customContent === 'function' ? 
            customContent({ handleClose }) : 
            customContent
        ) : (
          <Box sx={{ p: 2, minWidth: 250, maxWidth: 300 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
              {label}
            </Typography>
            {!hideSearch && (
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                {!isMarketSearch ? (
                  <TextField
                    inputRef={inputRef}
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);                                        
                    }}
                    placeholder={placeholder || `Search ${label}`}
                    size="small"
                    fullWidth
                    variant="outlined"
                    autoComplete='off'
                    onKeyDown={e => e.stopPropagation()}
                  />
                ) : (
                  <MarketSearchBar
                    OnBoardingSearchBox={OnBoardingSearchBox}
                    addMarketAfterSearch={value => {
                      if (setMarketSearchInfo && isFreeUser) {
                        setMarketSearchInfo({
                          searchName: value.details.searchName,
                          countryCode: value.details.countryCode,
                        });
                        handleClose();
                      }
                      setMarketComparisons && !isFreeUser && setMarketComparisons([value]);
                    }}
                  />
                )}
                <IconButton size="small" onClick={handleClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
            {multiSelect && Array.isArray(value) && selectedOptions.length > 0 && (
              <Box sx={{ my: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, overflowX: 'auto' }}>
                {!isMarketSearch && selectedOptions.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    onDelete={() => handleDeleteChip(opt.value)}
                    sx={{ height: 'auto', '& .MuiChip-label': { py: '2px' } }}
                  />
                ))}
              </Box>
            )}
            <List dense sx={{ maxHeight: 224, overflowY: 'auto' }}>
              {filteredOptions.length === 0 && search && isLocationFilter && onNoResultsAction ? (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      toggleSearchBar();
                    }}
                    sx={{my: '10px'}}
                    startIcon={<AddIcon />}
                  >                  
                    Add Specific {isMarketSearch ? (label === 'Location' ? 'Country' : 'Region') : 'Market'}
                  </Button>
                  {!isMarketSearch && <Typography variant="body2" sx={{ mb: 1 }}>                
                    No matching {label === 'Location' ? 'country' : 'region'} is found for "{search}".
                  </Typography>}                
                </Box>
              ) : filteredOptions.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No options found" />
                </ListItem>
              ) : null}
              {filteredOptions.map(opt => {
                const isSelected = multiSelect
                  ? Array.isArray(value) && value.includes(opt.value)
                  : value === opt.value;

                return (
                  <ListItemButton
                    key={opt.value}
                    dense
                    onClick={() => handleSelect(opt.value)}
                    selected={isSelected}
                    sx={{
                      height: '30px !important',
                      pl: opt.isChild ? 4 : multiSelect ? 0 : 2,
                      pr: opt.isExpandable ? 1 : multiSelect ? 2 : 2,
                    }}
                  >
                    {multiSelect && (
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      </ListItemIcon>
                    )}
                    <ListItemText primary={opt.label} />
                    {opt.isExpandable && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          setExpandedParents(prev =>
                            prev.includes(opt.value)
                              ? prev.filter(p => p !== opt.value)
                              : [...prev, opt.value]
                          );
                        }}
                        sx={{
                          ml: 1,
                        }}
                      >
                        {expandedParents.includes(opt.value) ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default FilterDropdown;
